import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import type { WaterRating, WaterRatingUpdate } from '@models/water-rating';
import { TranslatePipe } from '@ngx-translate/core';
import { fromDateOnly, toDateOnly } from '@utils/date-only';

export interface WaterRatingDialogData {
  rating: WaterRating;
}

interface RatingForm {
  name: FormControl<string>;
  email: FormControl<string>;
  amountOfFishermen: FormControl<number | null>;
  arrivalDate: FormControl<Date | null>;
  type: FormControl<string | null>;
  companyReview: FormControl<number>;
  waterReview: FormControl<number>;
  waterSideGuidanceReview: FormControl<number>;
  facilitiesReview: FormControl<number>;
  catches: FormControl<string | null>;
  tips: FormControl<string | null>;
}

const REVIEW_VALIDATORS = [Validators.required, Validators.min(1), Validators.max(10)];
const MAX_LIST_ITEMS = 3;

@Component({
  selector: 'app-water-rating-dialog',
  templateUrl: './water-rating-dialog.component.html',
  // Material chips default to a single, fixed-height line with nowrap text, so long
  // pros/cons overflow the dialog. Let the chip grow and wrap its label instead.
  styles: `
    :host ::ng-deep .mat-mdc-chip.mat-mdc-chip-row {
      height: auto;
    }
    :host ::ng-deep .mat-mdc-chip-action-label {
      white-space: normal;
      overflow-wrap: anywhere;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UpperCasePipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSliderModule,
    MatChipsModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    TranslatePipe,
  ],
})
export class WaterRatingDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<WaterRatingDialogComponent, WaterRatingUpdate>);
  private readonly data = inject<WaterRatingDialogData>(MAT_DIALOG_DATA);

  readonly rating = this.data.rating;
  readonly typeOptions = ['family', 'big-fish', 'friends'];
  readonly maxListItems = MAX_LIST_ITEMS;

  // Pros/cons are edited as chips (a small list of short tags), not free text, so they are
  // kept as signals rather than form controls and capped at MAX_LIST_ITEMS.
  readonly pros = signal<string[]>([...this.data.rating.pros]);
  readonly cons = signal<string[]>([...this.data.rating.cons]);

  readonly form = new FormGroup<RatingForm>({
    name: new FormControl(this.data.rating.name, {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    email: new FormControl(this.data.rating.email, {
      nonNullable: true,
      validators: [Validators.required, Validators.email, Validators.maxLength(255)],
    }),
    amountOfFishermen: new FormControl(this.data.rating.amountOfFishermen, [Validators.min(1)]),
    arrivalDate: new FormControl(fromDateOnly(this.data.rating.arrivalDate)),
    type: new FormControl(this.data.rating.type),
    companyReview: new FormControl(this.data.rating.companyReview, {
      nonNullable: true,
      validators: REVIEW_VALIDATORS,
    }),
    waterReview: new FormControl(this.data.rating.waterReview, { nonNullable: true, validators: REVIEW_VALIDATORS }),
    waterSideGuidanceReview: new FormControl(this.data.rating.waterSideGuidanceReview, {
      nonNullable: true,
      validators: REVIEW_VALIDATORS,
    }),
    facilitiesReview: new FormControl(this.data.rating.facilitiesReview, {
      nonNullable: true,
      validators: REVIEW_VALIDATORS,
    }),
    catches: new FormControl(this.data.rating.catches),
    tips: new FormControl(this.data.rating.tips),
  });

  addPro(event: MatChipInputEvent): void {
    this.addItem(this.pros, event);
  }

  removePro(index: number): void {
    this.pros.update((items) => items.filter((_, i) => i !== index));
  }

  addCon(event: MatChipInputEvent): void {
    this.addItem(this.cons, event);
  }

  removeCon(index: number): void {
    this.cons.update((items) => items.filter((_, i) => i !== index));
  }

  private addItem(target: WritableSignal<string[]>, event: MatChipInputEvent): void {
    const value = event.value.trim();
    if (value && target().length < MAX_LIST_ITEMS) {
      target.update((items) => [...items, value]);
    }
    event.chipInput.clear();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    this.dialogRef.close({
      name: raw.name.trim(),
      email: raw.email.trim(),
      amountOfFishermen: raw.amountOfFishermen,
      arrivalDate: toDateOnly(raw.arrivalDate),
      type: raw.type,
      companyReview: raw.companyReview,
      waterReview: raw.waterReview,
      waterSideGuidanceReview: raw.waterSideGuidanceReview,
      facilitiesReview: raw.facilitiesReview,
      catches: raw.catches,
      tips: raw.tips,
      pros: this.pros(),
      cons: this.cons(),
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
